import { COMPANY_PRIMARY_COLOR_PRESETS } from "@/lib/theme";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Palette, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export function CompanyPrimaryColorSection({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const currentColor = value?.trim() || "#f59e0b";

  return (
    <div className="rounded-xl border border-border/60 bg-muted/15 p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 border border-primary/20 text-primary shrink-0">
            <Palette className="h-4 w-4" />
          </div>
          <div>
            <Label className="text-xs font-bold text-foreground">Brand Primary Theme Color</Label>
            <p className="text-[11px] text-muted-foreground">
              Sets the main brand accent color across navigation, buttons, and active items.
            </p>
          </div>
        </div>
        {/* Live Preview Button */}
        <div className="flex items-center gap-2 border border-border/60 rounded-lg px-3 py-1.5 bg-background/80 shrink-0 shadow-2xs">
          <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Preview</span>
          <span
            className="h-4 w-4 rounded-full shadow-xs border border-black/10 shrink-0 transition-colors"
            style={{ backgroundColor: currentColor }}
          />
        </div>
      </div>

      {/* Preset Color Swatches */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {COMPANY_PRIMARY_COLOR_PRESETS.map((preset) => {
          const isSelected = currentColor.toLowerCase() === preset.value.toLowerCase();
          return (
            <button
              key={preset.value}
              type="button"
              onClick={() => onChange(preset.value)}
              className={cn(
                "group relative flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-all shadow-2xs",
                isSelected
                  ? "border-primary bg-primary/15 text-foreground ring-1 ring-primary"
                  : "border-border/60 bg-background/90 hover:border-primary/40 hover:bg-muted/50 text-muted-foreground",
              )}
              title={preset.label}
            >
              <span
                className="h-3.5 w-3.5 rounded-full border border-black/10 shrink-0 shadow-2xs"
                style={{ backgroundColor: preset.value }}
              />
              <span>{preset.label.split(" ")[0]}</span>
              {isSelected ? <Check className="h-3 w-3 text-primary shrink-0" /> : null}
            </button>
          );
        })}
      </div>

      {/* Custom Picker & Input */}
      <div className="flex items-center gap-3 pt-1">
        <Label className="text-xs text-muted-foreground shrink-0">Custom Hex Color:</Label>
        <div className="relative flex items-center gap-2 flex-1 max-w-xs">
          <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg border border-border/60 shadow-2xs cursor-pointer">
            <input
              type="color"
              value={currentColor.startsWith("#") ? currentColor : "#f59e0b"}
              onChange={(e) => onChange(e.target.value)}
              className="absolute -inset-2 h-14 w-14 cursor-pointer border-0 bg-transparent p-0"
              title="Custom color picker"
            />
          </div>
          <Input
            type="text"
            className="glass-input h-9 font-mono text-xs uppercase"
            value={currentColor}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#f59e0b"
          />
        </div>
      </div>
    </div>
  );
}
